// CSRF token management utility
const CSRF_TOKEN_KEY = 'csrf_token';

export const getCsrfToken = () => {
  return localStorage.getItem(CSRF_TOKEN_KEY);
};

export const setCsrfToken = (token) => {
  localStorage.setItem(CSRF_TOKEN_KEY, token);
};

export const removeCsrfToken = () => {
  localStorage.removeItem(CSRF_TOKEN_KEY);
};

// Function to fetch a new CSRF token from the server
export const fetchCsrfToken = async () => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/csrf-token`, {
      credentials: 'include', // Important for cookies
      method: 'GET',
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch CSRF token');
    }
    
    const data = await response.json();
    setCsrfToken(data.token);
    return data.token;
  } catch (error) {
    console.error('Error fetching CSRF token:', error);
    throw error;
  }
};

// Function to add CSRF token to request headers
export const addCsrfTokenToHeaders = (headers = {}) => {
  const token = getCsrfToken();
  return {
    ...headers,
    'X-CSRF-Token': token,
    'X-Requested-With': 'XMLHttpRequest'
  };
};

// Function to configure axios instance with CSRF protection
export const configureCsrfProtection = (axiosInstance) => {
  // Add request interceptor to add CSRF token to all requests
  axiosInstance.interceptors.request.use(async (config) => {
    // Don't add CSRF token to GET requests
    if (config.method !== 'get') {
      const token = getCsrfToken();
      if (!token) {
        // If no token exists, fetch a new one
        await fetchCsrfToken();
      }
      config.headers['X-CSRF-Token'] = getCsrfToken();
    }
    config.withCredentials = true;
    return config;
  });

  // Add response interceptor to handle CSRF token errors
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 403 && error.response?.data?.message === 'CSRF token invalid') {
        // If CSRF token is invalid, fetch a new one and retry the request
        await fetchCsrfToken();
        return axiosInstance(error.config);
      }
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};